/**
 * 一覧の型の絞りと合計（#302、2026-11-04 経理担当の指摘 2本目、docs/94 B-3 の追記・Figma「一覧の見直し（11/4）」①）。
 * 見積書・請求書・{deliveryNote}・入金の4つの一覧が同じ決まりで使う：
 *   ・期間の絞り（すべて／今月／先月／今期／期間指定。既定は すべて）＝帳票の日付（見積日・請求日・納品日・入金日）で絞る
 *   ・{customer}の絞り（取引先検索のドロアーで選ぶ）
 *   ・表の上の右端に絞った結果の「n件・税抜・税込」（請求書は残額の合計も・入金は入金額と未消込額）
 *   ・合計はタブ・期間・{customer}・絞り込み（検索）を全部かけた結果。タブの件数も期間・{customer}・検索をかけた数
 * {customer}の名前は略称を優先（空なら正式名称）＝listNameOf。紙・PDF・メール・取引先の詳細は正式名称のまま
 */
import { z } from "zod";
import { INVOICE_STATE_LABEL, type InvoiceState } from "./payment.js";

export const ListPeriod = z.enum(["all", "thisMonth", "lastMonth", "thisTerm", "range"]);
export type ListPeriod = z.infer<typeof ListPeriod>;

export const LIST_PERIOD_LABEL: Record<ListPeriod, string> = {
  all: "すべて",
  thisMonth: "今月",
  lastMonth: "先月",
  thisTerm: "今期",
  range: "期間指定",
};

/** 一覧の言葉（4つの一覧の列見出し・絞り・合計。Figma ③「案」） */
export const LIST_MSG = {
  period: "期間",
  periodFrom: "開始日",
  periodTo: "終了日",
  customerAll: "すべて",
  customerClear: "絞り解除",
  count: (n: number) => `${n.toLocaleString("ja-JP")}件`,
  subtotal: "税抜",
  total: "税込",
  balance: "残額",
  amount: "入金額",
  unallocated: "未消込額",
  /** 列見出し（{customer}・{subject}・{project}は会社の言葉。画面は t() を通す） */
  colQuoteDate: "見積日",
  colInvoiceDate: "請求日",
  colNoteDate: "納品日",
  colPayDate: "入金日",
  colNo: "番号",
  colState: "状態",
  colOrder: "受注",
  colBill: "請求",
  colDue: "支払期限",
  colTotal: "金額（税込）",
  colSubtotal: "金額（税抜）",
  colBalance: "残額",
  colPick: "選択",
  colInvoice: "請求書",
  colAmount: "入金額",
  colMethod: "方法",
  colUnallocated: "未消込額",
  /** 番号の列の下書き（全部の一覧で同じ。前は{deliveryNote}だけ「—」） */
  unissued: "未発行",
  /** 2行目：{project}名が{subject}と違うとき（#302 A3） */
  groupLine: (project: string, name: string) => `${project}：${name} ›`,
  /** 2行目：分割（見積書は請求した回／回数・請求書はその回／回数） */
  split: (n: number, m: number) => `分割 ${n}／${m}回`,
  closingMonth: (m: number) => `締め ${m}月分`,
  recurringMonth: (m: number) => `定期 ${m}月分`,
  /** 請求書作成（見積から／{deliveryNote}から／締め請求の3択） */
  invoiceCreate: "請求書作成",
  invoiceFromQuote: "見積から",
  invoiceFromNotes: "{deliveryNote}から",
  invoiceClosing: "締め請求",
  searchDoc: "番号・{customer}・{subject}で絞り込み",
  searchPayment: "{customer}・請求書番号で絞り込み",
  /** 乗せたときの説明（受注・失注を決めた日は2行目に出さない＝#302 A3） */
  decidedOn: (state: string, md: string) => `${md}に${state}`,
  /** 送付の列と絞り（#303 B3、経理担当の指摘 20） */
  colSend: "送付",
  sendFilter: "送付",
  sendAll: "すべて",
  /** 行の ⋯（#304）：送付を手で切り替える＝送付の記録に「手動」の行が1つ残る */
  rowMenu: "操作",
  markSent: "送付済にする",
  markUnsent: "未送付に戻す",
  markedSent: "送付済にしました。",
  markedUnsent: "未送付に戻しました。",
} as const;

/**
 * 送付の状態（#303 B3）：送付の記録（document_deliveries）から。未送付／送付済（印刷・PDF 保存・メールの送信・FAX）／取得済（メールの閲覧ページを開いた）。
 * 送信失敗・届いていません・送信待ちのメールは送付に数えない。請求書の一覧の列と絞り（見積書の一覧は同じ部品で後から）
 */
export const SendState = z.enum(["none", "sent", "opened"]);
export type SendState = z.infer<typeof SendState>;
export const SEND_STATE_LABEL: Record<SendState, string> = { none: "未送付", sent: "送付済", opened: "取得済" };
/** 送付の方法（乗せたときの説明）：印刷・PDF 保存・メール・FAX・手動（#304＝一覧の ⋯ の「送付済にする」）・その他（ネット・リンク） */
export const SendMethod = z.enum(["print", "download", "email", "fax", "manual", "other"]);
export type SendMethod = z.infer<typeof SendMethod>;
const SEND_METHOD_LABEL: Record<SendMethod, string> = { print: "印刷", download: "PDF 保存", email: "メール", fax: "FAX", manual: "手動", other: "送付" };
/**
 * 一覧の行の送付（API が送付の記録から作る）。at＝最後の送付の日時・openedAt＝閲覧ページを最初に開いた日時。
 * resetAt＝「未送付に戻す」（手動）の日時（#304）＝それより前の送付は数えない。未送付のまま乗せると「未送付に戻した 日付（手動）」
 */
export const SendSummary = z.object({ state: SendState, at: z.string().nullable(), method: SendMethod.nullable(), openedAt: z.string().nullable(), resetAt: z.string().nullable().optional() });
export type SendSummary = z.infer<typeof SendSummary>;
/** 絞り（URL の ?send=）。省けば すべて */
export const ListSendFilter = z.enum(["all", "none", "sent", "opened"]);
export type ListSendFilter = z.infer<typeof ListSendFilter>;
/**
 * 乗せたときの送付（#320 で形を改めた）：送付済「送付済 11/04（メール）」・取得済「取得済 11/05（メール 11/04）」・手動「送付済 11/04（手動）」・
 * 未送付に戻した「未送付に戻した 11/04（手動）」・未送付は空。日付は日本時間で、今年なら月日・違う年なら年2桁「25/11/04」。now はテスト用
 */
export function sendTitleOf(s: SendSummary | null | undefined, now: Date = new Date()): string {
  const jst = (d: Date) => new Date(d.getTime() + 9 * 3600_000);
  const year = jst(now).getUTCFullYear();
  const day = (iso: string) => {
    const d = jst(new Date(iso));
    const md = `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}`;
    return d.getUTCFullYear() === year ? md : `${String(d.getUTCFullYear() % 100).padStart(2, "0")}/${md}`;
  };
  if (s && s.state === "none" && s.resetAt) return `未送付に戻した ${day(s.resetAt)}（手動）`;
  if (!s || s.state === "none" || !s.at) return "";
  const how = SEND_METHOD_LABEL[s.method ?? "other"];
  return s.state === "opened" && s.openedAt ? `${SEND_STATE_LABEL.opened} ${day(s.openedAt)}（${how} ${day(s.at)}）` : `${SEND_STATE_LABEL.sent} ${day(s.at)}（${how}）`;
}

/** 請求書の一覧の「状態」の札の色（#320）：quiet＝薄墨（下書き・入金済・取消）／line＝藍の枠（未送付）／ai＝藍の淡い地（送付済・取得済・一部入金）／shu＝朱（期限超過） */
export type ListStateTone = "quiet" | "line" | "ai" | "shu";
/** 請求書の一覧の「状態」（札1つ＋2行目＋乗せたときの文） */
export type InvoiceListState = { label: string; tone: ListStateTone; sub: string | null; title: string };

/**
 * 請求書の一覧の「状態」（#320、2026-11-05 射場さん ★9・Figma 312:107）：前の「状態」「送付」の2列を札1つに（画面は表示だけ）。
 *   下書き →「下書き」／取消 →「取消」／期限超過 → 朱「期限超過」＋2行目「n日超過」（未送付なら「n日超過・未送付」）／
 *   入金済 →「入金済」／一部入金 →「一部入金」／発行済で未入金 → 送付の状態（未送付＝藍の枠・送付済・取得済）。
 *   乗せると送付と入金の両方（「送付済 11/04（メール）・未入金」）。入金を使わない会社（payState が無い）は送付の状態だけ
 */
export function invoiceListStateOf(x: { status: string; payState?: InvoiceState | null | undefined; overdueDays?: number | null | undefined; send?: SendSummary | null | undefined }, now: Date = new Date()): InvoiceListState {
  if (x.status === "draft") return { label: "下書き", tone: "quiet", sub: null, title: "" };
  if (x.status === "voided") return { label: "取消", tone: "quiet", sub: null, title: "" };
  const st = x.send?.state ?? "none";
  const sendText = sendTitleOf(x.send, now) || SEND_STATE_LABEL[st];
  const payText = x.payState ? (x.payState === "overdue" && x.overdueDays ? `${INVOICE_STATE_LABEL.overdue} ${x.overdueDays}日` : INVOICE_STATE_LABEL[x.payState]) : null;
  const title = [sendText, payText].filter(Boolean).join("・");
  if (x.payState === "overdue") {
    const days = x.overdueDays ? `${x.overdueDays}日超過` : "";
    const sub = [days, st === "none" ? SEND_STATE_LABEL.none : ""].filter(Boolean).join("・") || null;
    return { label: INVOICE_STATE_LABEL.overdue, tone: "shu", sub, title };
  }
  if (x.payState === "paid") return { label: INVOICE_STATE_LABEL.paid, tone: "quiet", sub: null, title };
  if (x.payState === "partial") return { label: INVOICE_STATE_LABEL.partial, tone: "ai", sub: null, title };
  return { label: SEND_STATE_LABEL[st], tone: st === "none" ? "line" : "ai", sub: null, title };
}
/** 「状態」の列の見出しの「？」（#320）：札の順 */
export const INVOICE_LIST_STATE_HELP = "下書き → 未送付 → 送付済・取得済 → 一部入金 → 入金済 の順に変わります。期限超過は朱の札で、超過した日数を下に出します。乗せると送付と入金の状態を表示します。";
/** 送付を手で切り替える（POST /invoices/:id/send-state、#304）。sent＝送付済にする／unsent＝未送付に戻す。今と同じ状態なら記録を足さない */
export const SendStateUpdate = z.object({ state: z.enum(["sent", "unsent"]) });
export type SendStateUpdate = z.infer<typeof SendStateUpdate>;

/** 絞りに合うか（すべては全部・未送付は none・送付済は sent と opened・取得済は opened） */
export const sendFilterHit = (f: ListSendFilter, s: SendSummary | null | undefined): boolean => {
  const st = s?.state ?? "none";
  return f === "all" ? true : f === "none" ? st === "none" : f === "sent" ? st !== "none" : st === "opened";
};

/** 一覧に出す{customer}の名前：略称（空白だけは空）を優先・無ければ正式名称（#302 A2。displayNameOf のように「株式会社」を外さない） */
export const listNameOf = (p: { name: string; shortName?: string | null }): string => (p.shortName ?? "").trim() || p.name;

/**
 * 帳票の画面の頭の宛先（#315、2026-11-05、docs/103 §8）：画面は「略称 ／ 支店 ／ 担当者」（取引先検索の選んだあとの1行と同じ形・敬称なし・略称が空なら正式名称）。
 * paper＝乗せたときの「紙の宛名：{正式名称} {支店} {担当者} {敬称}」（担当者があれば「様」・無ければ{customer}の敬称）。紙・PDF・メール・送付状は正式名称のまま
 */
export function headRecipientOf(c: { name: string; shortName?: string | null; honorific: string; branch: string | null; contact: string | null }): { name: string; branch: string | null; contact: string | null; text: string; paper: string } {
  const name = listNameOf(c);
  const branch = c.branch?.trim() || null;
  const contact = c.contact?.trim() || null;
  const tail = contact ? `${contact} 様` : c.honorific;
  return {
    name,
    branch,
    contact,
    text: [name, branch, contact].filter(Boolean).join(" ／ "),
    paper: `帳票の宛名：${[c.name, branch, tail].filter(Boolean).join(" ")}`,
  };
}

/** 'YYYY-MM-DD' の月の初日・末日 */
const firstOf = (y: number, m: number): string => `${y}-${String(m).padStart(2, "0")}-01`;
const lastOf = (y: number, m: number): string => {
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

/**
 * 期間の範囲（両端を含む・YYYY-MM-DD）。all は両方 null。range は渡した from・to（片方だけでもよい。逆なら入れ替える）。
 * 今期＝会計年度（fiscalStartMonth＝始まりの月。既定 4）の今の期の初日から末日まで
 */
export function listPeriodRange(kind: ListPeriod, today: string, fiscalStartMonth = 4, from?: string | null, to?: string | null): { from: string | null; to: string | null } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  switch (kind) {
    case "all":
      return { from: null, to: null };
    case "thisMonth":
      return { from: firstOf(y, m), to: lastOf(y, m) };
    case "lastMonth": {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: firstOf(py, pm), to: lastOf(py, pm) };
    }
    case "thisTerm": {
      const s = Math.min(12, Math.max(1, Math.trunc(fiscalStartMonth) || 4));
      const sy = m >= s ? y : y - 1;
      const ey = s === 1 ? sy : sy + 1;
      const em = s === 1 ? 12 : s - 1;
      return { from: firstOf(sy, s), to: lastOf(ey, em) };
    }
    case "range": {
      const f = from && /^\d{4}-\d{2}-\d{2}$/.test(from) ? from : null;
      const t = to && /^\d{4}-\d{2}-\d{2}$/.test(to) ? to : null;
      return f && t && f > t ? { from: t, to: f } : { from: f, to: t };
    }
  }
}

/** 日付（YYYY-MM-DD。日時の頭でもよい）が範囲に入るか */
export const inListPeriod = (date: string | null | undefined, r: { from: string | null; to: string | null }): boolean => {
  if (!r.from && !r.to) return true;
  if (!date) return false;
  const d = date.slice(0, 10);
  return (!r.from || d >= r.from) && (!r.to || d <= r.to);
};

/** 一覧の絞り込み（検索）の文字：大文字小文字・全角半角を同じに見る */
export const listQueryNorm = (s: string): string => s.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
/** どれかの文字に q を含むか（空なら true） */
export const listQueryHit = (q: string, ...texts: Array<string | null | undefined>): boolean => {
  const n = listQueryNorm(q);
  if (!n) return true;
  return texts.some((t) => t != null && listQueryNorm(t).includes(n));
};

/** 一覧の日付（2026/11/02）。Figma ① の形 */
export const ymdSlash = (iso: string | null | undefined): string => (iso ? `${iso.slice(0, 4)}/${iso.slice(5, 7)}/${iso.slice(8, 10)}` : "");
